/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: LGPL-2.1-only
 */
@import url("../shared.css");

:host {
  display: inline-block;
  isolation: isolate;

  --qds-main-subsection-card-title: var(
    --qds-main-subsection-card-standard-title-emphasized
  );
  --qds-main-subsection-card-subtitle: var(
    --qds-main-subsection-card-standard-subtitle
  );
  --qds-main-subsection-card-kicker: var(
    --qds-main-subsection-card-standard-kicker
  );
  --qds-web-card-min-width: 200px;
  --qds-web-card-max-width: 1424px;
  --qds-web-card-min-height: 338px;
  --qds-web-card-max-height: 600px;
  --qds-web-media-button-position-block-end: var(
    --qds-main-subsection-card-gap-children-unrelated
  );
  --qds-web-media-button-position-bottom: var(
    --qds-main-subsection-card-gap-children-unrelated
  );
}

.qds-web-title-tag-link {
  position: absolute;
  inset: 0;
  margin: 0;
  color: transparent;
  border-radius: var(--qds-control-border-width);
  z-index: -1;

  &:focus {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    outline-offset: var(--qds-focus-border-offset);
  }
}

:host(.qds-web-emphasized) {
  --qds-web-theme-focus-ring-light: rgb(0 117 163);

  height: 100%;
  width: 100%;
  min-width: var(--qds-web-card-min-width);
  max-width: var(--qds-web-card-max-width);
  min-height: var(--qds-web-card-min-height);
  max-height: var(--qds-web-card-max-height);

  .qds-web-title-tag-link {
    &:focus {
      outline-color: var(--qds-web-theme-focus-ring-light);
    }
  }
}

.flex {
  display: flex;
}

.hide {
  display: none;
}

.qds-web-wrapper {
  border-radius: var(--qds-main-subsection-border-radius);
  position: relative;
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;

  &.qds-web-card-hover {
    box-shadow: var(--qds-theme-main-subsection-emphasized);
    transform: translateY(-12px);
  }

  &.qds-web-card-pointer {
    cursor: pointer;
  }

  &.qds-web-wrapper-grid {
    height: 100%;
  }
}

@media (prefers-reduced-motion) {
  .qds-web-wrapper.qds-web-card-hover {
    box-shadow: unset;
    transform: unset;
  }
}

.qds-web-card {
  height: 100%;
  display: flex;
  align-items: flex-start;
  background-color: var(--qds-theme-main-subsection-standard-background);
  border-radius: var(--qds-main-subsection-border-radius);
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
  min-width: 400px;
  max-width: var(--qds-web-card-max-width);
  padding: var(--qds-main-subsection-padding);
  gap: var(--qds-main-subsection-gap-children-unrelated);
}

[data-appearance="outline"] {
  border: solid 1px var(--qds-theme-divider-subdued);
}

[data-appearance="transparent"] {
  background-color: transparent;
}

qds-web-media {
  border-radius: var(--qds-main-subsection-border-radius);
  height: 100%;
  min-width: 164px;
  max-width: 300px;
}

[data-ratio="16:9"] {
  qds-web-media {
    aspect-ratio: 16 / 9;
  }
}

[data-ratio="1:1"] {
  qds-web-media {
    aspect-ratio: 1 / 1;
  }
}

[data-ratio="3:2"] {
  qds-web-media {
    aspect-ratio: 3 / 2;
  }
}

.qds-web-content {
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--qds-main-subsection-card-gap-children-unrelated);
  padding: 0;
}

.qds-web-empty-content {
  .qds-web-content {
    display: none;
  }
}

.qds-web-media-section {
  width: 100%;
  min-width: 164px;
  max-width: 300px;
  position: relative;

  .qds-web-actions {
    position: absolute;
    justify-content: flex-end;
    width: 100%;
    box-sizing: border-box;
    inset-block-start: 0;
    padding-block-start: var(--qds-main-subsection-padding);
    padding-inline: var(--qds-main-subsection-padding);
  }

  .qds-web-is-selectable {
    justify-content: space-between;
  }

  .qds-web-logo {
    position: absolute;
    inset-block-end: 0;
    padding-block-end: var(--qds-main-subsection-padding);
    padding-inline-start: var(--qds-main-subsection-padding);
  }
}

.qds-web-no-media {
  display: flex;
  flex-direction: column;
  gap: 0;

  .qds-web-media-section,
  qds-checkbox,
  .qds-web-actions {
    padding: 0;
    position: static;
  }
}

[data-layout="list"] {
  &.qds-web-no-media {
    .qds-web-actions {
      padding-block-end: var(
        --qds-main-subsection-card-gap-children-unrelated,
      );
    }
  }
}

[data-layout="grid"] {
  &.qds-web-card {
    flex-direction: column;
    min-width: var(--qds-web-card-min-width);
    max-width: 700px;
    padding: 0;
    gap: 0;
  }

  &.qds-web-no-media {
    gap: 0;

    qds-checkbox {
      padding-inline-start: var(--qds-main-subsection-padding);
    }

    .qds-web-actions {
      padding-block-start: var(--qds-main-subsection-padding);
    }
  }

  .qds-web-media-section {
    min-width: var(--qds-web-card-min-width);
    max-width: none;
  }

  qds-web-media {
    width: 100%;
    min-width: var(--qds-web-card-min-width);
    max-width: none;
  }

  &[data-appearance="solid"] {
    qds-web-media {
      border-radius: var(--qds-main-subsection-border-radius)
        var(--qds-main-subsection-border-radius) 0 0;
    }

    .qds-web-content {
      padding: var(--qds-main-subsection-padding);
    }

    .qds-web-asset {
      padding-block: var(--qds-main-subsection-padding) 0;
      padding-inline: var(--qds-main-subsection-padding);
    }

    .qds-web-has-asset {
      &.qds-web-content {
        padding-block-start: 16px;
      }
    }
  }

  &[data-appearance="outline"] {
    qds-web-media {
      border-radius: var(--qds-main-subsection-border-radius)
        var(--qds-main-subsection-border-radius) 0 0;
    }

    .qds-web-content {
      padding: var(--qds-main-subsection-padding);
    }

    .qds-web-asset {
      padding-block: var(--qds-main-subsection-padding) 0;
      padding-inline: var(--qds-main-subsection-padding);
    }

    .qds-web-has-asset {
      &.qds-web-content {
        padding-block-start: 16px;
      }
    }
  }

  &[data-appearance="transparent"] {
    qds-web-media {
      border-radius: var(--qds-main-subsection-border-radius);
    }

    .qds-web-content {
      padding: var(--qds-main-subsection-padding) 0;
    }

    &.qds-web-no-media {
      qds-checkbox {
        padding: 0;
      }
    }

    .qds-web-has-asset {
      &.qds-web-content {
        padding-block: var(--qds-main-subsection-card-gap-children-unrelated,)
          var(--qds-main-subsection-gap-children-unrelated);
        padding-inline: 0;
      }
    }
  }

  &.qds-web-empty-content {
    qds-web-media {
      border-radius: var(--qds-main-subsection-border-radius);
    }
  }
}

[data-importance="emphasized"] {
  &[data-appearance="solid"] {
    &.qds-web-card {
      --qds-theme-main-subsection-standard-background: transparent;

      min-width: var(--qds-web-card-min-width);
      max-width: var(--qds-web-card-max-width);
      min-height: var(--qds-web-card-min-height);
      max-height: var(--qds-web-card-max-height);
    }

    .qds-web-media-section {
      min-width: var(--qds-web-card-min-width);
      max-width: var(--qds-web-card-max-width);
      min-height: var(--qds-web-card-min-height);
      height: 100%;
      max-height: var(--qds-web-card-max-height);
    }

    qds-web-media {
      position: absolute;
      min-width: var(--qds-web-card-min-width);
      max-width: var(--qds-web-card-max-width);
      min-height: var(--qds-web-card-min-height);
      max-height: var(--qds-web-card-max-height);
      border-radius: var(--qds-main-subsection-border-radius)
        var(--qds-main-subsection-border-radius);
    }

    .qds-web-actions {
      display: none;
    }

    .qds-web-emphasized-content {
      position: relative;
      min-width: var(--qds-web-card-min-width);
      max-width: var(--qds-web-card-max-width);
      min-height: var(--qds-web-card-min-height);
      max-height: var(--qds-web-card-max-height);
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: var(--qds-main-subsection-card-padding);
      border-radius: var(--qds-main-subsection-border-radius);
      background: linear-gradient(
        180deg,
        rgb(15 18 20 / 0%) 19.09%,
        rgb(15 18 20 / 90%) 100%
      );
      box-sizing: border-box;
    }

    .qds-web-justify-between {
      justify-content: space-between;
    }

    ::slotted([slot="tags"]) {
      display: flex;
      align-items: center;
      gap: var(--qds-control-standard-gap-siblings-related);
      flex-wrap: wrap;
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .qds-web-content-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--qds-main-subsection-card-gap-children-unrelated);
    }

    .qds-emphasized-expand {
      color: var(--qds-theme-title);
      max-height: 104px;
      overflow: hidden;
      display: block;
      position: relative;
      transition: all 0.3s ease-in-out;
      mask-image: linear-gradient(
        to bottom,
        rgb(0 0 0 / 100%) 94%,
        rgb(0 0 0 / 0%)
      );
      mask-position: 0 100%;
      mask-size: 100% 200%;
    }

    .qds-emphasized-focused {
      transition: all 0.5s ease-in-out;
      max-height: 500px;
      mask-position: 0 0;
    }

    @media (prefers-reduced-motion) {
      .qds-emphasized-expand {
        transition: unset;
        max-height: 500px;
        mask-position: 0 0;
      }
    }
  }
}

:host(:hover) .qds-emphasized-expand {
  transition: all 0.5s ease-in-out;
  max-height: 500px;
  mask-position: 0 0;
}

.qds-web-empty-actions {
  display: none;
  padding: 0;
}

.qds-web-title-tag {
  margin: 0;
}
